import { IntegrationType, NonSensitiveFlightRecordConnectedAccount } from "@nova/schemas"
import { ConnectedAccountIntegrationSection } from "./ConnectedAccountIntegrationSection"
import { usePilotTrackingConnectionsContext } from "features/data-management/pilot-tracking/PilotTrackingConnectionsContext"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { BeforeYouSyncModalAutel } from "../modals/sync/BeforeYouSyncModalAutel"
import { ConnectedAccountEditAutelModal } from "../modals/accounts/edit/ConnectedAccountEditAutelModal"
import { useCallback } from "react"
import * as Icons from "@nova/component-icons"
import { ContextMenuItem } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { ConnectedAccountDeleteAutelModal } from "../modals/accounts/delete/ConnectedAccountDeleteAutelModal"
import { useAppDispatch } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"

export const ConnectedAccountIntegrationSectionAutel = ({
    accounts,
}: {
    accounts: NonSensitiveFlightRecordConnectedAccount<IntegrationType.autel>[]
}) => {
    const dispatch = useAppDispatch()
    const confirmationModal = useConfirmationModalContext()
    const { triggerAutelSync, updateAutelConnection, deleteAutelConnection } =
        usePilotTrackingConnectionsContext()

    const showSyncModal = async (id: string) => {
        try {
            await confirmationModal.confirm({
                RenderComponent: BeforeYouSyncModalAutel,
                asyncConfirmCallback: () => triggerAutelSync(id),
            })
        } catch (err) {
            dispatch(
                setGlobalNotificationContext({
                    message: err instanceof Error ? err.message : "Failed to start sync",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
    }

    const showEditModal = useCallback(
        async (account: NonSensitiveFlightRecordConnectedAccount<IntegrationType.autel>) => {
            void confirmationModal.confirm({
                RenderComponent: (props) => (
                    <ConnectedAccountEditAutelModal
                        {...props}
                        account={account}
                        onSave={updateAutelConnection}
                        onDelete={() => deleteAutelConnection(account.id)}
                    />
                ),
            })
        },
        [confirmationModal, updateAutelConnection, deleteAutelConnection]
    )

    const showDeleteModal = useCallback(
        async (account: NonSensitiveFlightRecordConnectedAccount<IntegrationType.autel>) => {
            await confirmationModal.confirm({
                RenderComponent: (props) => (
                    <ConnectedAccountDeleteAutelModal
                        {...props}
                        account={account}
                        onDelete={() => deleteAutelConnection(account.id)}
                    />
                ),
            })
        },
        [confirmationModal, deleteAutelConnection]
    )

    const createContextMenuSections = useCallback(
        (
            account: NonSensitiveFlightRecordConnectedAccount<IntegrationType.autel>
        ): ContextMenuItem[][] => {
            return [
                [
                    {
                        title: "Edit Details",
                        Icon: Icons.Pencil,
                        onClick: () => showEditModal(account),
                    },
                ],
                [
                    {
                        title: "Disconnect Account",
                        Icon: Icons.LinkOff,
                        onClick: () => showDeleteModal(account),
                        isDestructive: true,
                    },
                ],
            ] satisfies ContextMenuItem[][]
        },
        [showEditModal, showDeleteModal]
    )

    return (
        <ConnectedAccountIntegrationSection
            type={IntegrationType.autel}
            accounts={accounts}
            onSync={showSyncModal}
            createContextMenuSections={createContextMenuSections}
        />
    )
}
